import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconPlus, IconRefresh, IconSearch, IconTrash } from '@/components/icons'
import { AppModal, EmptyState, InlineLoader, PaginationBar } from '@/components/ui'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import { ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type {
  ProjectSpamSettings,
  SpamBlockedSenderRow,
  SpamContentRule,
  SpamIdentifierType,
  SpamSource,
  SpamTicketRow,
} from '@/types/api'

type Props = {
  projectId: string
  canEdit: boolean
}

export const SPAM_SOURCE_LABELS: Record<SpamSource, string> = {
  manual: 'Вручную',
  blocked_sender: 'Заблокированный отправитель',
  flood: 'Флуд',
  rule: 'Правило по содержимому',
  sender_rate: 'Много обращений от отправителя',
}

const IDENTIFIER_LABELS: Record<SpamIdentifierType, string> = {
  email: 'Email',
  telegram_id: 'Telegram ID',
  customer_id: 'ID клиента',
  external_id: 'Внешний ID',
}

const PAGE_SIZE = 50

function notifyError(e: unknown) {
  showNotification({
    title: 'Ошибка',
    message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
    color: 'red',
  })
}

function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value.endsWith('Z') || value.includes('+') ? value : `${value}Z`)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('ru-RU')
}

export function ProjectSpamPanel({ projectId, canEdit }: Props) {
  const [tab, setTab] = useState<string | null>('settings')
  return (
    <Tabs value={tab} onChange={setTab} keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="settings">Настройки</Tabs.Tab>
        <Tabs.Tab value="tickets">Обращения в спаме</Tabs.Tab>
        <Tabs.Tab value="blocked">Заблокированные отправители</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="settings" pt="md">
        <SpamSettingsSection projectId={projectId} canEdit={canEdit} />
      </Tabs.Panel>
      <Tabs.Panel value="tickets" pt="md">
        <SpamTicketsSection projectId={projectId} />
      </Tabs.Panel>
      <Tabs.Panel value="blocked" pt="md">
        <BlockedSendersSection projectId={projectId} />
      </Tabs.Panel>
    </Tabs>
  )
}

function SpamSettingsSection({ projectId, canEdit }: Props) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ProjectSpamSettings | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setForm(await api.projectSpamSettings(projectId))
    } catch (e) {
      notifyError(e)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) return <InlineLoader label="Загрузка настроек" />
  if (!form) return null

  const set = <K extends keyof ProjectSpamSettings>(key: K, value: ProjectSpamSettings[K]) =>
    setForm({ ...form, [key]: value })
  const num = (key: keyof ProjectSpamSettings, label: string, description?: string, min = 0) => (
    <NumberInput
      label={label}
      description={description}
      min={min}
      value={form[key] as number}
      disabled={!canEdit || !form.enabled}
      onChange={(v) => set(key, (typeof v === 'number' ? v : Number(v) || 0) as never)}
    />
  )
  const setRule = (index: number, patch: Partial<SpamContentRule>) =>
    set(
      'content_rules',
      form.content_rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    )

  const save = async () => {
    setSaving(true)
    try {
      const rules = form.content_rules.filter((rule) => rule.pattern.trim())
      setForm(await api.projectSpamSettingsPut(projectId, { ...form, content_rules: rules }))
      showNotification({ title: 'Сохранено', message: 'Настройки защиты от спама обновлены.', color: 'green' })
    } catch (e) {
      notifyError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="md">
      <Card withBorder radius="sm" p="md">
        <Switch
          label="Автоматическая защита от спама"
          description="Флуд, правила по содержимому и лимит обращений. Заблокированные отправители действуют всегда."
          checked={form.enabled}
          disabled={!canEdit}
          onChange={(e) => set('enabled', e.currentTarget.checked)}
        />
      </Card>

      <Card withBorder radius="sm" p="md">
        <Text fw={700}>Флуд: пауза Мозгобота</Text>
        <Text size="xs" c="dimmed" mb="sm">
          Сообщения сохраняются, но не отправляются в Мозгобот, пока поток не стихнет. 0 — проверка выключена.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          {num('flood_window_seconds', 'Окно, сек', undefined, 10)}
          {num('flood_max_messages', 'Сообщений за окно', 'Больше — пауза')}
          {num('duplicate_max_count', 'Одинаковых за окно', 'Больше — пауза')}
          {num('ai_pause_minutes', 'Пауза, мин', undefined, 1)}
        </SimpleGrid>
      </Card>

      <Card withBorder radius="sm" p="md">
        <Switch
          label="Автоматически помечать спамом при сильном флуде"
          checked={form.auto_spam_enabled}
          disabled={!canEdit || !form.enabled}
          onChange={(e) => set('auto_spam_enabled', e.currentTarget.checked)}
          mb="sm"
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {num('auto_spam_messages_threshold', 'Сообщений в обращении', 'Достигнут порог — обращение уходит в спам')}
          {num('auto_spam_window_minutes', 'За период, мин', undefined, 1)}
        </SimpleGrid>
        <Checkbox
          mt="sm"
          label="Заодно блокировать отправителя"
          checked={form.auto_block_on_auto_spam}
          disabled={!canEdit || !form.enabled || !form.auto_spam_enabled}
          onChange={(e) => set('auto_block_on_auto_spam', e.currentTarget.checked)}
        />
      </Card>

      <Card withBorder radius="sm" p="md">
        <Text fw={700}>Лимит новых обращений от одного отправителя</Text>
        <Text size="xs" c="dimmed" mb="sm">
          Новые обращения сверх лимита сразу попадают в спам. 0 — без лимита.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {num('max_new_tickets_per_sender', 'Обращений')}
          {num('new_tickets_window_minutes', 'За период, мин', undefined, 1)}
        </SimpleGrid>
      </Card>

      <Card withBorder radius="sm" p="md">
        <Group justify="space-between" mb="sm">
          <div>
            <Text fw={700}>Правила по содержимому</Text>
            <Text size="xs" c="dimmed">
              Сообщение содержит текст или совпадает с регулярным выражением — обращение уходит в спам.
            </Text>
          </div>
          <Button
            variant="light"
            leftSection={<IconPlus size={14} />}
            disabled={!canEdit || !form.enabled}
            onClick={() =>
              set('content_rules', [...form.content_rules, { pattern: '', is_regex: false, enabled: true }])
            }
          >
            Добавить правило
          </Button>
        </Group>
        {form.content_rules.length ? (
          <Stack gap="xs">
            {form.content_rules.map((rule, index) => (
              <Group key={rule.id || `new-${index}`} gap="sm" wrap="nowrap" align="center">
                <TextInput
                  style={{ flex: 1 }}
                  placeholder={rule.is_regex ? 'Регулярное выражение, например bit\\.ly/\\w+' : 'Текст, например «онлайн казино»'}
                  value={rule.pattern}
                  disabled={!canEdit || !form.enabled}
                  onChange={(e) => setRule(index, { pattern: e.currentTarget.value })}
                />
                <Checkbox
                  label="Regex"
                  checked={rule.is_regex}
                  disabled={!canEdit || !form.enabled}
                  onChange={(e) => setRule(index, { is_regex: e.currentTarget.checked })}
                />
                <Switch
                  label="Вкл"
                  checked={rule.enabled}
                  disabled={!canEdit || !form.enabled}
                  onChange={(e) => setRule(index, { enabled: e.currentTarget.checked })}
                />
                <Button
                  variant="subtle"
                  color="red"
                  px="xs"
                  disabled={!canEdit}
                  aria-label="Удалить правило"
                  onClick={() =>
                    set(
                      'content_rules',
                      form.content_rules.filter((_, i) => i !== index),
                    )
                  }
                >
                  <IconTrash size={16} />
                </Button>
              </Group>
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">Правил нет.</Text>
        )}
      </Card>

      {canEdit ? (
        <Group justify="flex-end">
          <Button loading={saving} onClick={() => void save()}>
            Сохранить
          </Button>
        </Group>
      ) : (
        <Alert color="gray" variant="light">Изменять настройки может только администратор проектов.</Alert>
      )}
    </Stack>
  )
}

function SpamTicketsSection({ projectId }: { projectId: string }) {
  const { openNewTab } = useWorkspaceTabs()
  const [rows, setRows] = useState<SpamTicketRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [source, setSource] = useState<SpamSource | ''>('')
  const [loading, setLoading] = useState(true)
  const [restore, setRestore] = useState<SpamTicketRow | null>(null)
  const [unblockSender, setUnblockSender] = useState(true)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.projectSpamTickets(projectId, {
        search: appliedSearch,
        source,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      setRows(res.items)
      setTotal(res.total)
    } catch (e) {
      notifyError(e)
    } finally {
      setLoading(false)
    }
  }, [appliedSearch, page, projectId, source])

  useEffect(() => {
    void load()
  }, [load])

  const confirmRestore = async () => {
    if (!restore) return
    setBusy(true)
    try {
      await api.ticketNotSpam(restore.id, { unblock_sender: unblockSender && restore.sender_blocked })
      showNotification({ title: 'Готово', message: 'Обращение возвращено из спама.', color: 'green' })
      setRestore(null)
      await load()
    } catch (e) {
      notifyError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-end">
        <TextInput
          style={{ flex: 1, minWidth: 220 }}
          label="Поиск"
          placeholder="Номер, тема, имя, email, ID"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1)
              setAppliedSearch(search.trim())
            }
          }}
        />
        <Select
          label="Причина"
          placeholder="Все"
          clearable
          w={260}
          data={Object.entries(SPAM_SOURCE_LABELS).map(([value, label]) => ({ value, label }))}
          value={source || null}
          onChange={(v) => {
            setPage(1)
            setSource((v as SpamSource) || '')
          }}
        />
        <Button variant="default" leftSection={<IconRefresh size={14} />} onClick={() => void load()}>
          Обновить
        </Button>
      </Group>

      {loading ? (
        <InlineLoader label="Загрузка спама" />
      ) : rows.length ? (
        <Table.ScrollContainer minWidth={900}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Обращение</Table.Th>
                <Table.Th>Отправитель</Table.Th>
                <Table.Th>Причина</Table.Th>
                <Table.Th>Сообщений</Table.Th>
                <Table.Th>Помечено</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td maw={320}>
                    <Anchor
                      size="sm"
                      onClick={() => openNewTab(`/tickets/${encodeURIComponent(ticketPathSegmentFromListItem(row))}`)}
                    >
                      #{row.ticket_number}
                    </Anchor>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {row.subject}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{row.customer_name || row.customer_email || row.customer_external_id || '—'}</Text>
                    <Group gap={4}>
                      {row.channel_type ? (
                        <Text size="xs" c="dimmed">
                          {row.channel_name || row.channel_type}
                        </Text>
                      ) : null}
                      {row.sender_blocked ? (
                        <Badge size="xs" color="red" variant="light">
                          заблокирован
                        </Badge>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td maw={260}>
                    <Badge variant="light" color="gray">
                      {row.spam_source ? SPAM_SOURCE_LABELS[row.spam_source] : '—'}
                    </Badge>
                    {row.spam_reason ? (
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {row.spam_reason}
                      </Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td>{row.customer_messages_count}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDate(row.spam_marked_at)}</Text>
                    <Text size="xs" c="dimmed">
                      {row.spam_marked_by_name || 'автоматически'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => {
                        setUnblockSender(true)
                        setRestore(row)
                      }}
                    >
                      Не спам
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <EmptyState title="Спама нет" description="Здесь появятся обращения, помеченные как спам." />
      )}
      {total > PAGE_SIZE ? (
        <PaginationBar
          page={page}
          pageCount={Math.ceil(total / PAGE_SIZE)}
          total={total}
          onPageChange={setPage}
        />
      ) : null}

      <AppModal
        opened={Boolean(restore)}
        onClose={() => setRestore(null)}
        title="Вернуть обращение из спама?"
        footer={
          <>
            <Button variant="default" onClick={() => setRestore(null)}>
              Отмена
            </Button>
            <Button loading={busy} onClick={() => void confirmRestore()}>
              Не спам
            </Button>
          </>
        }
      >
        <Stack gap="sm">
          <Text size="sm">
            Обращение #{restore?.ticket_number} вернётся в общий поток: его снова обработает Мозгобот или получит оператор.
          </Text>
          {restore?.sender_blocked ? (
            <Checkbox
              label="Разблокировать отправителя"
              checked={unblockSender}
              onChange={(e) => setUnblockSender(e.currentTarget.checked)}
            />
          ) : null}
        </Stack>
      </AppModal>
    </Stack>
  )
}

function BlockedSendersSection({ projectId }: { projectId: string }) {
  const [active, setActive] = useState(true)
  const [rows, setRows] = useState<SpamBlockedSenderRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [newType, setNewType] = useState<SpamIdentifierType>('email')
  const [newValue, setNewValue] = useState('')
  const [newReason, setNewReason] = useState('')
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.projectSpamBlockedSenders(projectId, {
        active,
        search: appliedSearch,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      setRows(res.items)
      setTotal(res.total)
    } catch (e) {
      notifyError(e)
    } finally {
      setLoading(false)
    }
  }, [active, appliedSearch, page, projectId])

  useEffect(() => {
    void load()
  }, [load])

  const unblock = async (row: SpamBlockedSenderRow) => {
    setBusyId(row.id)
    try {
      await api.projectSpamUnblock(projectId, row.id)
      showNotification({ title: 'Готово', message: `${row.identifier_value} разблокирован.`, color: 'green' })
      await load()
    } catch (e) {
      notifyError(e)
    } finally {
      setBusyId(null)
    }
  }

  const add = async () => {
    if (!newValue.trim()) return
    setAdding(true)
    try {
      await api.projectSpamBlockSender(projectId, {
        identifier_type: newType,
        identifier_value: newValue.trim(),
        reason: newReason.trim() || undefined,
      })
      setAddOpen(false)
      setNewValue('')
      setNewReason('')
      if (!active) setActive(true)
      else await load()
    } catch (e) {
      notifyError(e)
    } finally {
      setAdding(false)
    }
  }

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-end">
        <TextInput
          style={{ flex: 1, minWidth: 220 }}
          label="Поиск"
          placeholder="Email, Telegram ID, имя, причина"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1)
              setAppliedSearch(search.trim())
            }
          }}
        />
        <Select
          label="Показать"
          w={200}
          allowDeselect={false}
          data={[
            { value: 'active', label: 'Заблокированы' },
            { value: 'history', label: 'Разблокированные' },
          ]}
          value={active ? 'active' : 'history'}
          onChange={(v) => {
            setPage(1)
            setActive(v !== 'history')
          }}
        />
        <Button leftSection={<IconPlus size={14} />} onClick={() => setAddOpen(true)}>
          Заблокировать
        </Button>
      </Group>

      {loading ? (
        <InlineLoader label="Загрузка" />
      ) : rows.length ? (
        <Table.ScrollContainer minWidth={820}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Отправитель</Table.Th>
                <Table.Th>Причина</Table.Th>
                <Table.Th>Заблокирован</Table.Th>
                {active ? null : <Table.Th>Разблокирован</Table.Th>}
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="sm">{row.identifier_value}</Text>
                    <Text size="xs" c="dimmed">
                      {IDENTIFIER_LABELS[row.identifier_type] || row.identifier_type}
                      {row.display_name ? ` · ${row.display_name}` : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td maw={280}>
                    <Text size="sm" lineClamp={2}>
                      {row.reason || '—'}
                    </Text>
                    {row.source === 'auto' ? (
                      <Badge size="xs" variant="light" color="orange">
                        автоматически
                      </Badge>
                    ) : null}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDate(row.created_at)}</Text>
                    <Text size="xs" c="dimmed">
                      {row.created_by_name || 'защита от спама'}
                    </Text>
                  </Table.Td>
                  {active ? null : (
                    <Table.Td>
                      <Text size="sm">{formatDate(row.unblocked_at)}</Text>
                      <Text size="xs" c="dimmed">
                        {row.unblocked_by_name || '—'}
                      </Text>
                    </Table.Td>
                  )}
                  <Table.Td>
                    {row.is_active ? (
                      <Button
                        size="xs"
                        variant="light"
                        loading={busyId === row.id}
                        onClick={() => void unblock(row)}
                      >
                        Разблокировать
                      </Button>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <EmptyState
          title={active ? 'Заблокированных нет' : 'История пуста'}
          description={active ? 'Отправители, заблокированные в обращениях или вручную, появятся здесь.' : 'Здесь появятся разблокированные отправители.'}
        />
      )}
      {total > PAGE_SIZE ? (
        <PaginationBar page={page} pageCount={Math.ceil(total / PAGE_SIZE)} total={total} onPageChange={setPage} />
      ) : null}

      <AppModal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        title="Заблокировать отправителя"
        footer={
          <>
            <Button variant="default" onClick={() => setAddOpen(false)}>
              Отмена
            </Button>
            <Button loading={adding} disabled={!newValue.trim()} onClick={() => void add()}>
              Заблокировать
            </Button>
          </>
        }
      >
        <Stack gap="sm">
          <Select
            label="Тип"
            allowDeselect={false}
            data={(['email', 'telegram_id', 'external_id', 'customer_id'] as SpamIdentifierType[]).map((value) => ({
              value,
              label: IDENTIFIER_LABELS[value],
            }))}
            value={newType}
            onChange={(v) => setNewType((v as SpamIdentifierType) || 'email')}
          />
          <TextInput
            label="Значение"
            placeholder={newType === 'email' ? 'spam@example.com' : '123456789'}
            value={newValue}
            onChange={(e) => setNewValue(e.currentTarget.value)}
          />
          <TextInput label="Причина" value={newReason} onChange={(e) => setNewReason(e.currentTarget.value)} />
          <Text size="xs" c="dimmed">
            Новые сообщения от отправителя сохраняются, но обращения сразу попадают в спам: без Мозгобота и операторов.
          </Text>
        </Stack>
      </AppModal>
    </Stack>
  )
}
